import * as FjPrimitives from 'components/primitives';
import { noop } from 'lodash-es';
import React, { Component } from 'react';
import styled from 'styled-components';

import { validateNotEmpty } from 'utils/validators';
import { Modal } from 'components/styled-primitives';

const Input = styled(FjPrimitives.FjInput).attrs(({ theme }) => ({
  borderColor: theme.colors.selectableLightBg,
}))`
  flex: 1;

  input {
    padding: 5px;
  }
`;

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
`;

const Extension = styled.span`
  color: ${({ theme }) => theme.colors.selectableLightBg};
  font-size: ${({ theme }) => theme.fontSizes.h5};
  margin-left: 5px;
`;

interface Validator {
  validator: (val: unknown) => boolean;
  getValidationMessage: () => string;
}

interface CancelConfig {
  /**
   * Cancel button text
   */
  text: string;
  /**
   * Callback after canceling (and closing modal)
   */
  callback: () => void;
}

interface AcceptConfig {
  /**
   * Confirmation button text
   */
  text: string;
  /**
   * Callback to handle downloading (after closing modal)
   */
  callback: (filename: string) => void;
}

interface DownloadConfirmationState {
  open: boolean;
  /**
   * Modal title text
   */
  title: string;
  /**
   * Initial filename text, if any
   */
  filename: string;
  /**
   * Optional validator -- defaults to check for empty filename
   */
  validation: Validator;
  /**
   * Optional extension string to display alongside filename input field
   */
  ext: string;
  cancel: CancelConfig;
  accept: AcceptConfig;
}

interface ConfirmConfig {
  title?: string;
  filename?: string;
  ext?: string;
  validator?: Validator;
  cancel?: Partial<CancelConfig>;
  accept?: Partial<AcceptConfig>;
}

class DownloadConfirmation extends Component<Record<string, never>, DownloadConfirmationState> {
  constructor(props: Record<string, never>) {
    super(props);
    this.state = this.getDefaultState();
  }

  getDefaultState = (): DownloadConfirmationState => ({
    open: false,
    title: '',
    filename: '',
    validation: validateNotEmpty,
    ext: '',
    cancel: {
      text: 'Cancel',
      callback: noop,
    },
    accept: {
      text: 'Download',
      callback: noop,
    },
  });

  // Public API: consuming app should set a ref on this component and call `thatRef.confirmDownload`
  confirm = (config: ConfirmConfig = {}): void => {
    this.setState((state) => ({
      open: true,
      title: config.title || '',
      filename: config.filename || '',
      ext: config.ext || '',
      validation: config.validator || validateNotEmpty,
      cancel: {
        ...state.cancel,
        ...(config.cancel || {}),
      },
      accept: {
        ...state.accept,
        ...(config.accept || {}),
      },
    }));
  };

  cancel = (): void => {
    const next = this.state.cancel.callback;
    this.setState(this.getDefaultState(), next);
  };

  accept = (): void => {
    const updatedFilename = this.state.filename;
    const next = this.state.accept.callback;
    this.setState(this.getDefaultState(), () => next(updatedFilename));
  };

  isValid = (): boolean => this.state.validation.validator(this.state.filename);

  updateFilename = (val: string): void =>
    this.setState({
      filename: val,
    });

  render(): React.ReactNode {
    if (!this.state.open) {
      return null;
    }

    return (
      <Modal
        cancel={{
          text: this.state.cancel.text,
          callback: this.cancel,
        }}
        accept={{
          disabled: !this.isValid(),
          text: this.state.accept.text,
          callback: this.accept,
        }}
        title={this.state.title}
      >
        <InputContainer>
          <Input
            validator={this.state.validation.validator}
            getValidationMessage={this.state.validation.getValidationMessage}
            value={this.state.filename}
            onChange={this.updateFilename}
          />
          {this.state.ext && <Extension>.{this.state.ext}</Extension>}
        </InputContainer>
        <p className="disclaimer margin-bottom-0">
          If your file name contains unusual punctuation, your browser may change it in final
          download.
        </p>
      </Modal>
    );
  }
}

export default DownloadConfirmation;
